import React, { useState, useMemo } from 'react';
import { PREPARATIONS, BIODYNAMIC_INTRO } from '@/data/biodynamic';
import { DAY_TYPE_META, MOON_PHASE_EMOJI } from '@/types/biodynamic';
import {
  getDayAstronomy,
  getMoonZodiacSign,
  getMoonPhaseData,
  isMoonAscending,
  getMonthNodeDays,
  getMonthApsides,
  ZODIAC_SYMBOLS,
  ZODIAC_ELEMENT,
  type PlanetaryPosition,
} from '@/lib/astronomy';
import { ZODIAC_TO_DAY_TYPE } from '@/types/biodynamic';
import { Link } from 'react-router-dom';
import { moonPath, ascendingInHemisphere } from '@/lib/hemisphere';
import { useHemisphere } from '@/context/HemisphereContext';
import PageHeader from '@/components/ui/PageHeader';
import AdUnit from '@/components/ads/AdUnit';
import { usePageMeta } from '@/hooks/usePageMeta';
import './BiodynamicPage.css';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const ELEMENT_LABEL: Record<string, string> = {
  fire:  'warmth · fruit',
  earth: 'earth · root',
  air:   'light · flower',
  water: 'water · leaf',
};

export default function BiodynamicPage() {
  const { hemisphere } = useHemisphere();
  usePageMeta(
    'Moon Planting & Biodynamic Calendar',
    'Moon planting calendar showing root, leaf, flower and fruit days with the current moon phase, plus all eight biodynamic preparations (500–508) explained.',
  );
  const [activeSection, setActiveSection] = useState<'calendar' | 'preparations' | 'about'>('calendar');
  const [selectedPrep, setSelectedPrep] = useState<string | null>(null);
  const today = new Date();

  return (
    <div className="biodynamic-page">
      <PageHeader
        title="Biodynamic Growing"
        subtitle="Grow with cosmic rhythms. Follow the lunar planting calendar and learn the art of biodynamic preparations."
        eyebrow="Lunar & cosmic rhythms"
        decorationEmoji="🌕"
        actions={
          <Link
            to={moonPath(today.getFullYear(), today.getMonth(), hemisphere)}
            className="btn-sage"
          >
            🌙 {MONTH_NAMES[today.getMonth()]} moon calendar
          </Link>
        }
      />

      {/* Section nav */}
      <div className="bio-section-nav">
        <div className="container">
          <div className="bio-section-tabs">
            {([
              { key: 'calendar',     label: '🗓 Planting Calendar' },
              { key: 'preparations', label: '🧪 Preparations' },
              { key: 'about',        label: '📖 About Biodynamics' },
            ] as const).map(tab => (
              <button
                key={tab.key}
                className={`bio-section-tab ${activeSection === tab.key ? 'bio-section-tab--active' : ''}`}
                onClick={() => setActiveSection(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container">
        <div className="page-ad"><AdUnit adSlot="1570177744" width={160} height={600} /></div>
        {activeSection === 'calendar'     && <CalendarSection today={today} />}
        {activeSection === 'preparations' && <PreparationsSection selectedPrep={selectedPrep} setSelectedPrep={setSelectedPrep} />}
        {activeSection === 'about'        && <AboutSection />}
      </div>
    </div>
  );
}

// ── Calendar section ────────────────────────────────────────────────────────
function CalendarSection({ today }: { today: Date }) {
  const { hemisphere } = useHemisphere();
  const [offset, setOffset] = useState(0);
  const displayDate = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const month       = displayDate.getMonth();
  const year        = displayDate.getFullYear();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow    = new Date(year, month, 1).getDay();
  const startOffset = firstDow === 0 ? 6 : firstDow - 1; // Monday-first grid

  // Today's full astronomical data (computed once)
  const todayData = useMemo(() => getDayAstronomy(today), [today]);
  const todayMeta = DAY_TYPE_META[todayData.moonDayType];
  // Ascending/descending is the one reading that reverses below the equator.
  const todayAscending = ascendingInHemisphere(todayData.moonAscending, hemisphere);

  // Month-level events (computed once per month view)
  const nodeDayMap = useMemo(() => {
    const map: Record<number, 'ascending' | 'descending'> = {};
    getMonthNodeDays(year, month).forEach(n => { map[n.day] = n.kind; });
    return map;
  }, [year, month]);

  const apsisDayMap = useMemo(() => {
    const map: Record<number, 'perigee' | 'apogee'> = {};
    getMonthApsides(year, month).forEach(a => { map[a.day] = a.type; });
    return map;
  }, [year, month]);

  // Per-day calendar data
  const calendarDays = useMemo(() =>
    Array.from({ length: daysInMonth }).map((_, i) => {
      const date     = new Date(year, month, i + 1, 12);
      const sign     = getMoonZodiacSign(date);
      const { phase } = getMoonPhaseData(date);
      const ascending = ascendingInHemisphere(isMoonAscending(date), hemisphere);
      return { date, dayType: ZODIAC_TO_DAY_TYPE[sign], phase, ascending };
    }),
  [year, month, daysInMonth, hemisphere]);

  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <div className="bio-calendar-section section">

      {/* ── Today highlight ── */}
      <div className="bio-today-highlight animate-fade-in">

        {/* Date */}
        <div className="bio-today-date">
          <span className="bio-today-day text-hand-lg">{today.getDate()}</span>
          <span className="bio-today-month text-label">{MONTH_NAMES[today.getMonth()]}</span>
        </div>

        {/* Day type */}
        <div className={`bio-today-type bio-today-type--${todayData.moonDayType}`}>
          <span className="bio-today-type-emoji">{todayMeta.emoji}</span>
          <div>
            <p className="text-hand-lg">{todayMeta.label}</p>
            <p className="text-small" style={{ color: 'var(--ink-light)' }}>{todayMeta.description}</p>
            <span className="bio-today-zodiac-tag">
              Moon in {capitalize(todayData.moonZodiacSign)} {todayData.moonZodiacSymbol}
              {' '}· {todayData.moonSignDegrees}°
              {' '}· {ZODIAC_ELEMENT[todayData.moonZodiacSign]}
            </span>
          </div>
        </div>

        {/* Moon phase */}
        <div className="bio-today-moon">
          <span className="bio-moon-large">{MOON_PHASE_EMOJI[todayData.moonPhase]}</span>
          <div>
            <p className="text-hand">{todayData.moonPhase.replace(/-/g, ' ')}</p>
            <p className="text-small" style={{ color: 'var(--ink-faint)' }}>
              {todayData.moonIllumination}% illuminated
            </p>
            <p className="text-small" style={{ color: 'var(--ink-faint)' }}>
              {todayAscending ? '↗ ascending' : '↘ descending'}
            </p>
          </div>
        </div>

        {/* Advice + warnings */}
        <div className="bio-today-advice">
          <p className="text-hand" style={{ color: todayAscending ? 'var(--sage)' : 'var(--terracotta)' }}>
            {todayAscending ? '🌱 Ideal for planting & sowing' : '🌿 Ideal for harvesting & pruning'}
          </p>
          <p className="text-small" style={{ color: 'var(--ink-faint)', marginTop: '2px' }}>
            Best: {todayMeta.description.split('—')[1]?.trim() ?? todayMeta.description}
          </p>
          {todayData.avoidPlanting && (
            <div className="bio-today-warning">
              ⚠️ {todayData.nearNode
                ? `Lunar ${todayData.nodeKind} node — avoid sowing`
                : 'Near perigee (Pg) — seeds may struggle'}
            </div>
          )}
          {todayData.nearApogee && (
            <div className="bio-today-info">
              ☽ Near apogee (Ag) — plants may bolt early
            </div>
          )}
        </div>
      </div>

      {/* ── Planetary positions ── */}
      <PlanetaryStrip positions={todayData.planetaryPositions} />

      {/* ── Month navigation ── */}
      <div className="bio-month-nav">
        <button className="cal-nav-btn" onClick={() => setOffset(o => o - 1)}>
          ← {MONTH_NAMES[(month - 1 + 12) % 12]}
        </button>
        <h2 className="text-title">{MONTH_NAMES[month]} {year}</h2>
        <button className="cal-nav-btn" onClick={() => setOffset(o => o + 1)}>
          {MONTH_NAMES[(month + 1) % 12]} →
        </button>
      </div>

      {/* ── Month grid ── */}
      <div className="bio-month-grid">
        {/* Day headers */}
        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
          <div key={d} className="bio-cal-header text-caption">{d}</div>
        ))}
        {/* Empty lead cells */}
        {Array.from({ length: startOffset }).map((_, i) => (
          <div key={`empty-${i}`} className="bio-cal-cell bio-cal-cell--empty" />
        ))}
        {/* Day cells */}
        {calendarDays.map((day, i) => {
          const dayNum  = i + 1;
          const isToday = day.date.toDateString() === today.toDateString();
          const dtMeta  = DAY_TYPE_META[day.dayType];
          const nodeKind  = nodeDayMap[dayNum];
          const apsisType = apsisDayMap[dayNum];
          const hasEvent  = nodeKind !== undefined || apsisType !== undefined;

          return (
            <div
              key={i}
              className={`bio-cal-cell bio-cal-cell--${day.dayType} ${isToday ? 'bio-cal-cell--today' : ''} ${nodeKind !== undefined ? 'bio-cal-cell--node' : ''}`}
              title={hasEvent
                ? nodeKind !== undefined
                  ? `Lunar ${nodeKind} node (☊/☋) — avoid sowing`
                  : apsisType === 'perigee' ? 'Perigee — seeds may struggle' : 'Apogee — plants may bolt'
                : undefined}
            >
              <span className="bio-cal-day-num">{dayNum}</span>
              <span className="bio-cal-moon">{MOON_PHASE_EMOJI[day.phase]}</span>
              <span className="bio-cal-type-icon">{dtMeta.emoji}</span>
              {hasEvent && (
                <span className="bio-cal-event">
                  {nodeKind !== undefined
                    ? nodeKind === 'ascending' ? '☊' : '☋'
                    : apsisType === 'perigee' ? 'Pg' : 'Ag'}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Legend ── */}
      <div className="bio-calendar-legend">
        {Object.entries(DAY_TYPE_META).map(([type, meta]) => (
          <div key={type} className={`bio-legend-item bio-legend-item--${type}`}>
            <span className="bio-legend-emoji">{meta.emoji}</span>
            <div>
              <p className="text-hand" style={{ marginBottom: '2px' }}>{meta.label}</p>
              <p className="text-small" style={{ color: 'var(--ink-faint)' }}>{meta.description}</p>
            </div>
          </div>
        ))}
        <div className="bio-legend-item bio-legend-item--events">
          <span className="bio-legend-emoji" style={{ fontSize: '1rem', lineHeight: '2' }}>☊ ☋ Pg Ag</span>
          <div>
            <p className="text-hand" style={{ marginBottom: '2px' }}>Special events</p>
            <p className="text-small" style={{ color: 'var(--ink-faint)' }}>
              ☊ Ascending node · ☋ Descending node (avoid sowing) · Pg Perigee · Ag Apogee
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Planetary strip ─────────────────────────────────────────────────────────
function PlanetaryStrip({ positions }: { positions: PlanetaryPosition[] }) {
  if (positions.length === 0) return null;
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <div className="bio-planets-strip paper">
      <p className="bio-planets-heading text-label">Planetary positions today</p>
      <p className="text-small" style={{ color: 'var(--ink-faint)', marginBottom: 'var(--space-4)' }}>
        Each planet carries its element's influence through the sign it occupies, subtly amplifying that day's biodynamic quality.
      </p>
      <div className="bio-planets-row">
        {positions.map(p => (
          <div key={p.planet} className={`bio-planet-card bio-planet-card--${p.element}`}>
            <span className="bio-planet-symbol">{p.symbol}</span>
            <div className="bio-planet-info">
              <span className="bio-planet-name text-caption">{p.planet}</span>
              <span className="bio-planet-sign">
                {p.zodiacSymbol} {capitalize(p.zodiacSign)}
              </span>
              <span className="bio-planet-element text-caption">{ELEMENT_LABEL[p.element]}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Preparations section ────────────────────────────────────────────────────
function PreparationsSection({ selectedPrep, setSelectedPrep }: { selectedPrep: string | null; setSelectedPrep: (id: string | null) => void }) {
  const active = selectedPrep ? PREPARATIONS.find(p => p.id === selectedPrep) : null;

  return (
    <div className="bio-preps-section section">
      <div className="bio-preps-intro">
        <h2 className="text-headline">The Eight Biodynamic Preparations</h2>
        <p className="text-body" style={{ color: 'var(--ink-light)', maxWidth: '60ch', marginTop: 'var(--space-3)' }}>
          The biodynamic preparations (numbered 500–508) are unique medicinal substances used in tiny quantities to activate soil life, compost fermentation, and plant health. They were formulated by Rudolf Steiner in 1924 and have been refined by practitioners ever since.
        </p>
      </div>

      <div className="bio-preps-grid">
        {PREPARATIONS.map((prep, i) => (
          <button
            key={prep.id}
            className={`bio-prep-card animate-fade-in-delay-${Math.min(i + 1, 5)} ${selectedPrep === prep.id ? 'bio-prep-card--active' : ''}`}
            onClick={() => setSelectedPrep(selectedPrep === prep.id ? null : prep.id)}
          >
            <div className="bio-prep-number text-display">{prep.id}</div>
            <div className="bio-prep-content">
              <h3 className="text-title">{prep.name}</h3>
              <p className="bio-prep-common text-hand">{prep.commonName}</p>
              <p className="text-small" style={{ color: 'var(--ink-light)' }}>{prep.ingredients.join(', ')}</p>
            </div>
          </button>
        ))}
      </div>

      {active && (
        <div className="bio-prep-detail paper animate-fade-in">
          <div className="bio-prep-detail-header">
            <div>
              <span className="text-display" style={{ color: 'var(--sage-light)', fontSize: '3rem', lineHeight: 1 }}>{active.id}</span>
              <h2 className="text-headline">{active.name}</h2>
              <p className="text-subtitle">{active.commonName}</p>
            </div>
            <button className="bio-prep-close" onClick={() => setSelectedPrep(null)}>×</button>
          </div>

          <p className="text-body" style={{ color: 'var(--ink-light)', margin: 'var(--space-4) 0' }}>{active.description}</p>

          <div className="bio-prep-sections">
            <div>
              <h3 className="text-title" style={{ marginBottom: 'var(--space-3)' }}>How to make</h3>
              <div className="prose">
                {active.howToMake.split('\n\n').map((para, i) => <p key={i}>{para}</p>)}
              </div>
            </div>
            <div>
              <h3 className="text-title" style={{ marginBottom: 'var(--space-3)' }}>How to use</h3>
              <div className="prose">
                {active.howToUse.split('\n\n').map((para, i) => <p key={i}>{para}</p>)}
              </div>
              <div className="note" style={{ marginTop: 'var(--space-4)' }}>
                <strong>Timing:</strong> {active.timing}
              </div>
            </div>
          </div>

          <div className="bio-prep-benefits">
            <h3 className="text-title" style={{ marginBottom: 'var(--space-3)' }}>Benefits</h3>
            <ul className="bio-benefits-list">
              {active.benefits.map(b => <li key={b} className="text-body">✓ {b}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ── About section ───────────────────────────────────────────────────────────
function AboutSection() {
  return (
    <div className="bio-about-section section">
      <div className="bio-about-layout">

        {/* ── Main content ── */}
        <div className="bio-about-prose">

          {/* Introduction */}
          <blockquote className="bio-steiner-quote">
            "One is amazed to find how much the peasant knows of what is actually going on in Nature. It is no longer possible today to write such a 'Peasant Philosophy' — too much of the real thing has been lost."
            <cite>— Rudolf Steiner, Agriculture Course, 1924</cite>
          </blockquote>

          <div className="prose">
            <p>Biodynamic farming and gardening was developed by Rudolf Steiner in 1924, following requests from farmers who were noticing declining soil fertility and plant health after the introduction of chemical fertilisers. Steiner delivered a series of lectures — the "Agriculture Course" — at Koberwitz, which formed the foundation of the movement.</p>
            <p>Biodynamic practice views the farm or garden as a self-sustaining living organism, with soil, plants, animals, and cosmic forces all deeply interconnected. It goes beyond organic farming to embrace a spiritual-scientific understanding of agriculture rooted in Anthroposophy — the idea that humanity once held an instinctive, reverent relationship with the cosmos, and that recovering this connection is essential to growing truly vital food.</p>
          </div>

          {/* The Moon's two rhythms */}
          <div className="bio-guide-section">
            <h2 className="text-headline bio-guide-heading">The Moon's Two Rhythms</h2>
            <p className="text-body" style={{ color: 'var(--ink-light)', marginBottom: 'var(--space-6)' }}>
              There are two distinct ways the Moon influences plant growth, and understanding both is key to reading a biodynamic calendar.
            </p>
            <div className="bio-rhythm-compare">
              <div className="bio-rhythm-card bio-rhythm-card--synodic">
                <div className="bio-rhythm-icon">🌑🌕</div>
                <h3 className="text-title">Synodic Rhythm</h3>
                <p className="bio-rhythm-subtitle text-hand">The phases of the Moon</p>
                <p className="text-small" style={{ color: 'var(--ink-light)' }}>
                  The 29.5-day cycle from new Moon to full Moon and back. Governs moisture in the soil — the waxing Moon draws water and life force upward through the plant; the waning Moon draws forces downward toward the roots.
                </p>
                <div className="bio-rhythm-rules">
                  <p className="text-caption" style={{ fontWeight: 600, marginBottom: 'var(--space-2)' }}>WAXING (new → full)</p>
                  <p className="text-small" style={{ color: 'var(--ink-light)' }}>Sow above-ground crops — leaf, flower, fruit</p>
                  <p className="text-caption" style={{ fontWeight: 600, margin: 'var(--space-3) 0 var(--space-2)' }}>WANING (full → new)</p>
                  <p className="text-small" style={{ color: 'var(--ink-light)' }}>Sow root crops, transplant, prune, harvest</p>
                </div>
              </div>
              <div className="bio-rhythm-card bio-rhythm-card--sidereal">
                <div className="bio-rhythm-icon">♈♉♊</div>
                <h3 className="text-title">Sidereal Rhythm</h3>
                <p className="bio-rhythm-subtitle text-hand">The Moon in the zodiac</p>
                <p className="text-small" style={{ color: 'var(--ink-light)' }}>
                  The 27.3-day cycle as the Moon moves through the twelve constellations of the fixed stars. Maria Thun's research showed this is the dominant influence on which part of the plant is stimulated — root, leaf, flower, or fruit.
                </p>
                <div className="bio-rhythm-rules">
                  <p className="text-caption" style={{ fontWeight: 600, marginBottom: 'var(--space-2)' }}>USED BY</p>
                  <p className="text-small" style={{ color: 'var(--ink-light)' }}>The Maria Thun Biodynamic Calendar — the standard reference for serious biodynamic growers worldwide</p>
                  <p className="text-caption" style={{ fontWeight: 600, margin: 'var(--space-3) 0 var(--space-2)' }}>THIS CALENDAR</p>
                  <p className="text-small" style={{ color: 'var(--ink-light)' }}>Uses sidereal rhythm via a precise astronomical ephemeris</p>
                </div>
              </div>
            </div>
          </div>

          {/* Lili Kolisko */}
          <div className="bio-guide-section">
            <h2 className="text-headline bio-guide-heading">Lili Kolisko's Research — The Synodic Moon</h2>
            <div className="prose">
              <p>Lili Kolisko (1889–1976) was one of the first scientists to rigorously test Rudolf Steiner's agricultural indications. From 1926 onwards she conducted extensive sowing experiments comparing seeds planted at different phases of the Moon, and her results were remarkable.</p>
            </div>

            <div className="bio-kolisko-findings">
              <div className="bio-kolisko-card">
                <span className="bio-kolisko-icon">🌕</span>
                <div>
                  <p className="text-hand" style={{ color: 'var(--sage)' }}>Two days before full Moon</p>
                  <p className="text-small" style={{ color: 'var(--ink-light)' }}>Seeds sprouted quickly. Plants were strong and healthy. Tomatoes weighed 280–350g. Peas and beans produced crops up to 80% larger.</p>
                </div>
              </div>
              <div className="bio-kolisko-card">
                <span className="bio-kolisko-icon">🌑</span>
                <div>
                  <p className="text-hand" style={{ color: 'var(--terracotta)' }}>Two days before new Moon</p>
                  <p className="text-small" style={{ color: 'var(--ink-light)' }}>Seeds took much longer to sprout, often waiting in the soil for the next waxing Moon. Tomatoes weighed only 120–180g. Plants developed poorly.</p>
                </div>
              </div>
              <div className="bio-kolisko-card">
                <span className="bio-kolisko-icon">🥕</span>
                <div>
                  <p className="text-hand" style={{ color: 'var(--sage)' }}>The carrot taste test</p>
                  <p className="text-small" style={{ color: 'var(--ink-light)' }}>Full Moon carrots were smooth-skinned, watery, and sweet. New Moon carrots were wrinkled, dry, and bitter. "It seems to be a natural law that full Moon forces bring more of the watery element into the fruits."</p>
                </div>
              </div>
              <div className="bio-kolisko-card">
                <span className="bio-kolisko-icon">🌲</span>
                <div>
                  <p className="text-hand" style={{ color: 'var(--ochre)' }}>Cutting trees with the Moon</p>
                  <p className="text-small" style={{ color: 'var(--ink-light)' }}>Trees cut at full Moon are saturated with sap and the wood is almost impossible to dry. Trees cut during the waning Moon dry easily and make superior timber for furniture or fuel.</p>
                </div>
              </div>
            </div>

            <blockquote className="bio-steiner-quote bio-steiner-quote--small">
              "At full Moon the forces of the Moon are just beginning to decrease. Two days before the Moon is full, we are in a stream of energy striving towards the maximum strength. It is therefore necessary to sow two or three days before the Moon is full if we want to benefit from all the Moon can allow to stream into the life-forces of plants."
              <cite>— Lili Kolisko</cite>
            </blockquote>
          </div>

          {/* Maria Thun & the Sidereal Moon */}
          <div className="bio-guide-section">
            <h2 className="text-headline bio-guide-heading">Maria Thun's Research — The Sidereal Moon</h2>
            <div className="prose">
              <p>Maria Thun (1922–2012) spent 60 years conducting meticulous sowing trials on her farm in Germany. Her central discovery was that the Moon's position against the <em>fixed stars of the zodiac</em> — not simply its phase — is the dominant influence on plant development. She called this the sidereal rhythm.</p>
              <p>Through thousands of trial plots she found that the same plant, sown under different zodiac constellations, developed in markedly different ways — as a root type, a leaf type, a flower type, or a fruit and seed type. "The fourfold nature of the plant became evident," she wrote. This observation, she believed, reflected a basic law of plant formation.</p>
            </div>

            {/* Fourfold plant grid */}
            <div className="bio-fourfold-grid">
              <div className="bio-fourfold-card bio-fourfold-card--root">
                <div className="bio-fourfold-header">
                  <span className="bio-fourfold-emoji">🌱</span>
                  <div>
                    <h3 className="text-title">Root Days</h3>
                    <p className="text-hand" style={{ color: 'var(--terracotta)' }}>Earth element</p>
                  </div>
                </div>
                <p className="bio-fourfold-signs text-caption">♉ Taurus · ♍ Virgo · ♑ Capricorn</p>
                <p className="text-small" style={{ color: 'var(--ink-light)' }}>
                  Best for root vegetables — carrots, beetroot, potatoes, parsnips, onions, celeriac. Root crops sown on Root days develop the best yields: high quality, good flavour, and they store well through winter. Root crops sown on Leaf days, by contrast, produce large leaves but small, poorly-keeping roots.
                </p>
              </div>

              <div className="bio-fourfold-card bio-fourfold-card--leaf">
                <div className="bio-fourfold-header">
                  <span className="bio-fourfold-emoji">🥬</span>
                  <div>
                    <h3 className="text-title">Leaf Days</h3>
                    <p className="text-hand" style={{ color: 'var(--sage)' }}>Water element</p>
                  </div>
                </div>
                <p className="bio-fourfold-signs text-caption">♋ Cancer · ♏ Scorpio · ♓ Pisces</p>
                <p className="text-small" style={{ color: 'var(--ink-light)' }}>
                  Best for leafy greens — lettuce, spinach, chard, kale, cabbage, herbs grown for leaf. Plants sown under water constellations develop abundant foliage and remain in the leaf stage for an extended period, which is ideal for crops harvested for their leaves. Also good for applying Preparation 508 (horsetail).
                </p>
              </div>

              <div className="bio-fourfold-card bio-fourfold-card--flower">
                <div className="bio-fourfold-header">
                  <span className="bio-fourfold-emoji">🌸</span>
                  <div>
                    <h3 className="text-title">Flower Days</h3>
                    <p className="text-hand" style={{ color: 'var(--rose, var(--sage-light))' }}>Air / Light element</p>
                  </div>
                </div>
                <p className="bio-fourfold-signs text-caption">♊ Gemini · ♎ Libra · ♒ Aquarius</p>
                <p className="text-small" style={{ color: 'var(--ink-light)' }}>
                  Best for flowering plants, brassicas, and cutting flowers. Plants sown under air constellations develop many branches, numerous flower buds, and remain in the flowering stage for a prolonged period. When flowers are cut, the plants readily produce new side shoots. Wine experts at Tesco and Marks & Spencer found wines tasted significantly better on Flower (and Fruit) days.
                </p>
              </div>

              <div className="bio-fourfold-card bio-fourfold-card--fruit">
                <div className="bio-fourfold-header">
                  <span className="bio-fourfold-emoji">🍅</span>
                  <div>
                    <h3 className="text-title">Fruit Days</h3>
                    <p className="text-hand" style={{ color: 'var(--ochre)' }}>Fire / Warmth element</p>
                  </div>
                </div>
                <p className="bio-fourfold-signs text-caption">♈ Aries · ♌ Leo · ♐ Sagittarius</p>
                <p className="text-small" style={{ color: 'var(--ink-light)' }}>
                  Best for fruiting crops — tomatoes, courgettes, beans, peas, cucumbers, peppers, apples, grapes. Aries and Sagittarius were particularly favourable for cucumbers, tomatoes, and beans. Peas and broad beans thrived best when sown under Leo. Fruit days are also considered the best days for wine tasting.
                </p>
              </div>
            </div>
          </div>

          {/* Planets & Elements */}
          <div className="bio-guide-section">
            <h2 className="text-headline bio-guide-heading">The Planets and the Elements</h2>
            <div className="prose">
              <p>Maria Thun extended her research beyond the Moon to study the influence of all the planets of the solar system. Each planet is associated with one of the four classical elements — warmth (fire), earth, air/light, and water — and when a planet occupies a constellation of its corresponding element, it strengthens that element's influence on the Earth and on plant growth.</p>
              <p>Planetary oppositions, conjunctions, and trines also affect plant development. Steiner noted that the forces of individual planets can even be seen in flowers: "In the red rose we see the forces of Mars; in blue flowers the force of Saturn; and in white and yellow flowers the forces of Jupiter."</p>
            </div>

            <div className="bio-planets-guide-table">
              <div className="bio-pgt-row bio-pgt-row--header">
                <span>Element</span>
                <span>Planets</span>
                <span>Constellations</span>
                <span>Plant part</span>
              </div>
              <div className="bio-pgt-row bio-pgt-row--fire">
                <span className="bio-pgt-element">🔥 Warmth</span>
                <span>Saturn ♄  Mercury ☿  Pluto</span>
                <span>♈ Aries · ♌ Leo · ♐ Sagittarius</span>
                <span className="bio-pgt-type">Fruit &amp; seed</span>
              </div>
              <div className="bio-pgt-row bio-pgt-row--earth">
                <span className="bio-pgt-element">🌍 Earth</span>
                <span>Sun ☉</span>
                <span>♉ Taurus · ♍ Virgo · ♑ Capricorn</span>
                <span className="bio-pgt-type">Root</span>
              </div>
              <div className="bio-pgt-row bio-pgt-row--air">
                <span className="bio-pgt-element">🌬 Air / Light</span>
                <span>Jupiter ♃  Venus ♀  Uranus ♅</span>
                <span>♊ Gemini · ♎ Libra · ♒ Aquarius</span>
                <span className="bio-pgt-type">Flower</span>
              </div>
              <div className="bio-pgt-row bio-pgt-row--water">
                <span className="bio-pgt-element">💧 Water</span>
                <span>Moon ☾  Mars ♂  Neptune ♆</span>
                <span>♋ Cancer · ♏ Scorpio · ♓ Pisces</span>
                <span className="bio-pgt-type">Leaf</span>
              </div>
            </div>

            <p className="text-small" style={{ color: 'var(--ink-faint)', marginTop: 'var(--space-4)' }}>
              When Jupiter is in Gemini (an air sign), for example, it brings a doubled air/light influence. When Saturn is in Aries (a fire sign), it creates a strong affinity for warmth. These planetary rhythms complement and sometimes override the lunar zodiac signal — which is why the biodynamic calendar is always changing and no two days are ever quite the same.
            </p>
          </div>

          {/* Ascending & Descending Moon */}
          <div className="bio-guide-section">
            <h2 className="text-headline bio-guide-heading">Ascending &amp; Descending Moon</h2>
            <div className="prose">
              <p>Separate from the Moon's phase, there is a further 27-day rhythm in which the Moon rises to a higher zenith in the sky for roughly two weeks (ascending), then sinks lower for the following two weeks (descending). This is sometimes confused with the waxing and waning phases, but they are entirely different cycles.</p>
            </div>
            <div className="bio-asc-desc-grid">
              <div className="bio-asc-card">
                <span className="bio-asc-arrow">↗</span>
                <div>
                  <h3 className="text-title">Ascending Moon</h3>
                  <p className="text-hand" style={{ color: 'var(--sage)', marginBottom: 'var(--space-2)' }}>Sowing &amp; planting time</p>
                  <p className="text-small" style={{ color: 'var(--ink-light)' }}>
                    The Moon's sap-drawing forces work <em>above the soil</em>. Plant forces and sap flow rise more strongly, filling the plant with vitality. Ideal for sowing seeds, planting seedlings, taking cuttings, and grafting. Harvested produce will be full of life force.
                  </p>
                </div>
              </div>
              <div className="bio-asc-card">
                <span className="bio-asc-arrow bio-asc-arrow--down">↘</span>
                <div>
                  <h3 className="text-title">Descending Moon</h3>
                  <p className="text-hand" style={{ color: 'var(--terracotta)', marginBottom: 'var(--space-2)' }}>Transplanting &amp; root work</p>
                  <p className="text-small" style={{ color: 'var(--ink-light)' }}>
                    The Moon's forces work <em>within the soil</em>. Plants orientate themselves toward the root. Ideal for transplanting (roots quickly re-establish), applying Preparation 500 (horn manure) and compost, pruning fruit trees, and cutting hedges. Sap flow is weaker, reducing bleed from pruning cuts.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Nodes, Perigee, Apogee */}
          <div className="bio-guide-section">
            <h2 className="text-headline bio-guide-heading">Lunar Nodes, Perigee &amp; Apogee</h2>
            <div className="prose">
              <p>Beyond the zodiac sign and ascending/descending rhythm, the biodynamic calendar marks several special lunar events that require caution or awareness. Maria Thun observed these effects through decades of sowing trials.</p>
            </div>
            <div className="bio-lunar-events">
              <div className="bio-lunar-event bio-lunar-event--avoid">
                <div className="bio-lunar-event-symbol">☊ ☋</div>
                <div>
                  <h3 className="text-title">Lunar Nodes — Avoid</h3>
                  <p className="text-small" style={{ color: 'var(--ink-light)', marginTop: 'var(--space-2)' }}>
                    The Moon's ascending node (☊) is where it crosses the ecliptic moving northward; the descending node (☋) where it moves southward. At both nodes, the Moon's path crosses the Earth's orbital plane, causing a temporary disruption in the plant's ability to receive solar and lunar energy. Repeated observations showed that seeds sown within a few hours of a node consistently produced deformed or weakened growth — "a serious decline in the quality of the seed, even going as far as a breakdown of regenerative powers" (Maria Thun, 1977). Avoid sowing or major garden work for 3 hours either side of the exact node. On this calendar, node days are marked ☊ or ☋ with a dashed outline.
                  </p>
                </div>
              </div>
              <div className="bio-lunar-event bio-lunar-event--perigee">
                <div className="bio-lunar-event-symbol">Pg</div>
                <div>
                  <h3 className="text-title">Perigee — Use with Care</h3>
                  <p className="text-small" style={{ color: 'var(--ink-light)', marginTop: 'var(--space-2)' }}>
                    Perigee is the Moon's closest point to Earth in its elliptical orbit. At perigee, the Moon's gravitational pull is at its strongest. Thun observed that seeds sown around perigee may struggle to germinate or lie dormant, and growth tends to be suppressed. Avoid sowing for 12 hours before and after the exact perigee. Perigee days are marked <strong>Pg</strong> on this calendar.
                  </p>
                </div>
              </div>
              <div className="bio-lunar-event bio-lunar-event--apogee">
                <div className="bio-lunar-event-symbol">Ag</div>
                <div>
                  <h3 className="text-title">Apogee — Watch for Bolting</h3>
                  <p className="text-small" style={{ color: 'var(--ink-light)', marginTop: 'var(--space-2)' }}>
                    Apogee is the Moon's furthest point from Earth. At apogee, plants may bolt or rush toward flower and seed formation early — similar to the effect of midsummer heat. Apogee does not override a fire sign constellation to create a Fruit day; treat it simply as a note of caution. Apogee days are marked <strong>Ag</strong> on this calendar. Note: apogee does not override fire-sign constellations to create Flower days.
                  </p>
                </div>
              </div>
              <div className="bio-lunar-event bio-lunar-event--trine">
                <div className="bio-lunar-event-symbol">△</div>
                <div>
                  <h3 className="text-title">Trines — Enhancement</h3>
                  <p className="text-small" style={{ color: 'var(--ink-light)', marginTop: 'var(--space-2)' }}>
                    When two planets are 120° apart in the zodiac (a trine), their combined elemental influence can amplify or even overrule the Moon's sign. A trine between planets of the same element as the current Moon sign creates a particularly potent day for that plant type. The effect extends 12 hours before and 6 hours after the exact trine.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* How to use this calendar */}
          <div className="bio-guide-section">
            <h2 className="text-headline bio-guide-heading">How to Use This Calendar</h2>
            <div className="prose">
              <p>The Maria Thun Biodynamic Calendar is the gold standard reference — published each year with exact constellation times and recommendations from decades of research. This calendar aims to reflect the same principles using a live astronomical ephemeris.</p>
              <p>For best results, consult the calendar the day before you plan to sow or plant. Check:</p>
              <ol>
                <li><strong>Day type</strong> (Root, Leaf, Flower, or Fruit) — choose the type that matches what you're growing.</li>
                <li><strong>Moon ascending or descending</strong> — ascending for sowing, descending for transplanting and root work.</li>
                <li><strong>Any warnings</strong> — avoid node days (☊ ☋) and perigee (Pg) for sowing. Take note of apogee (Ag).</li>
                <li><strong>Planetary positions</strong> — check whether the major planets reinforce or alter the day's dominant element.</li>
              </ol>
              <p>Above all, observe your own garden. Biodynamic research strongly encourages growers to carry out their own trials and come to their own understanding. The cosmos is new at every moment — there is never an exact repeat.</p>
            </div>
          </div>

        </div>

        {/* ── Sidebar ── */}
        <aside className="bio-about-sidebar">
          <div className="paper" style={{ padding: 'var(--space-6)' }}>
            <h3 className="text-title" style={{ marginBottom: 'var(--space-4)' }}>Key figures</h3>
            <div className="bio-figures">
              <div className="bio-figure">
                <p className="text-hand-lg" style={{ color: 'var(--sage)' }}>Rudolf Steiner</p>
                <p className="text-small" style={{ color: 'var(--ink-faint)' }}>1861–1925. Austrian philosopher, founder of Anthroposophy. Delivered the Agriculture Course at Koberwitz in 1924, founding biodynamic agriculture.</p>
              </div>
              <div className="bio-figure">
                <p className="text-hand-lg" style={{ color: 'var(--sage)' }}>Lili Kolisko</p>
                <p className="text-small" style={{ color: 'var(--ink-faint)' }}>1889–1976. Pioneering biodynamic researcher. Her decades of sowing experiments — comparing crops sown at different Moon phases — provided the first rigorous scientific evidence for lunar influence on plant growth. She continued her work alone in England in extreme poverty after her husband's early death.</p>
              </div>
              <div className="bio-figure">
                <p className="text-hand-lg" style={{ color: 'var(--sage)' }}>Maria Thun</p>
                <p className="text-small" style={{ color: 'var(--ink-faint)' }}>1922–2012. German researcher who spent 60 years investigating the sidereal rhythm of the Moon and its influence on root, leaf, flower, and fruit development. Creator of the internationally used Biodynamic Planting Calendar.</p>
              </div>
              <div className="bio-figure">
                <p className="text-hand-lg" style={{ color: 'var(--sage)' }}>Demeter International</p>
                <p className="text-small" style={{ color: 'var(--ink-faint)' }}>The certification body for biodynamic farms and products worldwide, founded in 1928. The Demeter logo guarantees fully biodynamic practices on certified holdings.</p>
              </div>
            </div>
          </div>

          <div className="paper" style={{ padding: 'var(--space-6)', marginTop: 'var(--space-4)' }}>
            <h3 className="text-title" style={{ marginBottom: 'var(--space-4)' }}>Zodiac quick reference</h3>
            <div className="bio-zodiac-ref">
              {[
                { sign: '♈ Aries',       element: 'Fire',  type: 'Fruit' },
                { sign: '♉ Taurus',      element: 'Earth', type: 'Root' },
                { sign: '♊ Gemini',      element: 'Air',   type: 'Flower' },
                { sign: '♋ Cancer',      element: 'Water', type: 'Leaf' },
                { sign: '♌ Leo',         element: 'Fire',  type: 'Fruit' },
                { sign: '♍ Virgo',       element: 'Earth', type: 'Root' },
                { sign: '♎ Libra',       element: 'Air',   type: 'Flower' },
                { sign: '♏ Scorpio',     element: 'Water', type: 'Leaf' },
                { sign: '♐ Sagittarius', element: 'Fire',  type: 'Fruit' },
                { sign: '♑ Capricorn',   element: 'Earth', type: 'Root' },
                { sign: '♒ Aquarius',    element: 'Air',   type: 'Flower' },
                { sign: '♓ Pisces',      element: 'Water', type: 'Leaf' },
              ].map(row => (
                <div key={row.sign} className={`bio-zodiac-ref-row bio-zodiac-ref-row--${row.type.toLowerCase()}`}>
                  <span className="bio-zodiac-ref-sign text-small">{row.sign}</span>
                  <span className="bio-zodiac-ref-type text-caption">{row.type}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="paper" style={{ padding: 'var(--space-6)', marginTop: 'var(--space-4)' }}>
            <h3 className="text-title" style={{ marginBottom: 'var(--space-4)' }}>Further reading</h3>
            <ul className="bio-reading-list">
              <li><em>Agriculture Course</em> — Rudolf Steiner (1924)</li>
              <li><em>Work on the Land and the Constellations</em> — Maria Thun</li>
              <li><em>Maria Thun's Biodynamic Almanac</em> — published annually</li>
              <li><em>Biodynamic Farming and Gardening</em> — Ehrenfried Pfeiffer</li>
              <li><em>The Biodynamic Farm</em> — Herbert Koepf</li>
              <li><em>Grasp the Nettle</em> — Peter Proctor</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
